import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  UploadCloud, 
  FileText, 
  X, 
  Sparkles, 
  Zap, 
  ShieldCheck, 
  Loader2,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useApiClient } from "@/hooks/use-api-client";
import { toast } from "sonner";
import { useDropzone } from "react-dropzone";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export default function PlaybookCreation() {
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [file, setFile] = useState<File | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState<"upload" | "generating" | "success">("upload");
  const [generatedSkill, setGeneratedSkill] = useState<any>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    maxFiles: 1
  });

  const handleGenerate = async () => {
    if (!file) {
      toast.error("Por favor, selecciona un archivo de playbook.");
      return;
    }

    setStep("generating");
    setIsGenerating(true);
    setProgress(10);

    try {
      // Step 1: Read file content
      setProgress(20);
      const textContent = await file.text();
      
      setProgress(40);
      // Step 2: Send to backend for skill generation
      const localizedContent = `[IMPORTANT: All legal references MUST be based on Colombian law. Specifically, use Art. 1602 of the Colombian Civil Code for contractual force, and NEVER reference Art. 1255 of the Spanish Civil Code or any other foreign jurisdiction.]\n\n${textContent}`;
      
      const res = await apiClient.post('/api/v1/contract-review/generate-skill', {
        playbook_text: localizedContent,
        playbook_name: file.name,
        source_filename: file.name
      });

      if (res.ok) {
        setProgress(100);
        setGeneratedSkill(res.data.skill);
        setStep("success");
        toast.success("¡Skill generado exitosamente!");
      } else {
        throw new Error(res.data?.message || "Error al generar el skill");
      }
    } catch (err: any) {
      console.error("Error generating skill:", err);
      toast.error(err.message || "No se pudo procesar el playbook. Intenta nuevamente.");
      setStep("upload");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-[#f8fafc] min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 p-6 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard/skills")}
            className="text-slate-500 hover:text-slate-900 rounded-xl"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a Skills
          </Button>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-purple-600 border-purple-100 bg-purple-50">
              Nuevo Playbook
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto w-full p-8 mt-4">
        {step === "upload" && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center space-y-3">
              <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">
                Crea una nueva <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">Habilidad Legal</span>
              </h1>
              <p className="text-slate-500 text-lg max-w-xl mx-auto">
                Sube tu playbook o guía de negociación y nuestra IA extraerá las reglas, criterios y mejores prácticas automáticamente.
              </p>
            </div>

            {/* Beginner Guidance Box */}
            <div className="bg-amber-50 border border-amber-100 rounded-3xl p-6 flex gap-5 items-start shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <HelpCircle className="h-6 w-6 text-amber-600" />
              </div>
              <div className="space-y-2">
                <h4 className="font-bold text-amber-900 text-lg">¿Primera vez creando un Playbook?</h4>
                <p className="text-amber-800/80 leading-relaxed">
                  No necesitas un documento ultra-complejo. Un Playbook es simplemente una lista de tus <strong>preferencias legales</strong>. 
                  Por ejemplo: <br/>
                  <span className="italic opacity-80">"Siempre limitamos la responsabilidad al 100% del valor del contrato"</span> o <br/>
                  <span className="italic opacity-80">"No aceptamos jurisdicción fuera de Colombia"</span>.
                </p>
                <p className="text-amber-900 font-semibold pt-1">
                  Cualquier documento con tus reglas de negocio sirve como punto de partida.
                </p>
              </div>
            </div>

            <div 
              {...getRootProps()} 
              className={cn(
                "relative group overflow-hidden border-2 border-dashed rounded-[2.5rem] p-16 transition-all duration-300 cursor-pointer text-center",
                isDragActive ? "border-purple-500 bg-purple-50/50 scale-[1.01]" : "border-gray-200 bg-white hover:border-purple-300 hover:bg-slate-50/50"
              )}
            >
              <input {...getInputProps()} />
              
              <div className="relative z-10 flex flex-col items-center">
                <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mb-6 shadow-xl shadow-purple-200 group-hover:scale-110 transition-transform duration-300">
                  <UploadCloud className="h-10 w-10 text-white" />
                </div>
                
                {file ? (
                  <div className="space-y-2">
                    <p className="text-2xl font-bold text-slate-900">{file.name}</p>
                    <p className="text-slate-500">{(file.size / 1024).toFixed(1)} KB • Listo para procesar</p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}
                      className="mt-4 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl"
                    >
                      <X className="mr-1 h-4 w-4" /> Eliminar
                    </Button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Arrastra tu Playbook aquí</h3>
                    <p className="text-slate-500 text-lg">o haz clic para seleccionar un archivo</p>
                    <div className="mt-8 flex gap-4">
                      <Badge variant="secondary" className="bg-white border-gray-100 text-slate-400 font-medium">PDF</Badge>
                      <Badge variant="secondary" className="bg-white border-gray-100 text-slate-400 font-medium">DOCX</Badge>
                      <Badge variant="secondary" className="bg-white border-gray-100 text-slate-400 font-medium">TXT</Badge>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
              <div className="flex items-start gap-4 p-4">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Zap className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">Extracción Rápida</h4>
                  <p className="text-xs text-slate-500 mt-1">IA avanzada que procesa documentos complejos en segundos.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">Reglas Dinámicas</h4>
                  <p className="text-xs text-slate-500 mt-1">Identificación automática de cláusulas, riesgos y excepciones.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">Integración Total</h4>
                  <p className="text-xs text-slate-500 mt-1">Usa tus skills en Revisión de Contratos y otros agentes.</p>
                </div>
              </div>
            </div>

            <div className="pt-8 flex justify-center">
              <Button 
                onClick={handleGenerate}
                disabled={!file || isGenerating}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold py-8 px-12 rounded-2xl shadow-xl shadow-purple-200 transition-all hover:scale-105 disabled:opacity-50 disabled:scale-100 w-full md:w-auto"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-3 h-6 w-6 animate-spin" />
                    Procesando Playbook...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-3 h-6 w-6" />
                    Generar Habilidad con IA
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === "generating" && (
          <div className="flex flex-col items-center justify-center py-20 space-y-8 animate-in fade-in duration-500">
            <div className="relative">
              <div className="w-32 h-32 rounded-[2rem] bg-purple-100 flex items-center justify-center animate-pulse">
                <Sparkles className="h-16 w-16 text-purple-600" />
              </div>
              <div className="absolute -bottom-2 -right-2 w-12 h-12 rounded-2xl bg-white shadow-lg flex items-center justify-center">
                <Loader2 className="h-6 w-6 text-purple-600 animate-spin" />
              </div>
            </div>
            
            <div className="text-center space-y-3 w-full max-w-md">
              <h2 className="text-3xl font-extrabold text-slate-900">Analizando Conocimiento</h2>
              <p className="text-slate-500">Nuestro agente legal está estructurando las reglas de tu playbook...</p>
              
              <div className="pt-6 space-y-2">
                <div className="flex justify-between text-sm font-bold text-slate-700">
                  <span>Progreso de Análisis</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} className="h-3 bg-purple-100" />
              </div>
            </div>
          </div>
        )}

        {step === "success" && (
          <div className="space-y-8 animate-in zoom-in-95 duration-500">
            <div className="bg-white rounded-[3rem] p-12 border border-gray-100 shadow-2xl shadow-purple-100 text-center space-y-6">
              <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-12 w-12 text-green-600" />
              </div>
              
              <div className="space-y-2">
                <h2 className="text-4xl font-extrabold text-slate-900">¡Skill Creado!</h2>
                <p className="text-slate-500 text-lg">
                  La habilidad <span className="font-bold text-slate-900">"{generatedSkill?.name}"</span> ha sido integrada a tu biblioteca.
                </p>
              </div>

              <div className="bg-slate-50 rounded-3xl p-8 text-left border border-slate-100 mt-8">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="h-6 w-6 text-purple-600" />
                  <h4 className="font-bold text-slate-900">Resumen de la Habilidad</h4>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  {generatedSkill?.description || "Se han extraído reglas de cumplimiento y mejores prácticas basadas en tu documento."}
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  <Badge className="bg-purple-100 text-purple-700 border-none">Reglas de Cumplimiento</Badge>
                  <Badge className="bg-blue-100 text-blue-700 border-none">Análisis de Riesgos</Badge>
                  <Badge className="bg-emerald-100 text-emerald-700 border-none">Estándares Legales</Badge>
                </div>
              </div>

              <div className="pt-6 flex flex-col md:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate("/dashboard/skills")}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-bold py-6 px-10 rounded-2xl transition-all"
                >
                  Ir al Centro de Skills
                </Button>
                <Button 
                  onClick={() => navigate("/dashboard/agents/contract-review-v2")}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-6 px-10 rounded-2xl shadow-lg shadow-purple-200 transition-all"
                >
                  Usar en Revisión de Contratos
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
